/**
 * REST client for the Spendly API (same origin: /api/v1).
 * - Access token: memory only (gone on reload; restored via the refresh cookie).
 * - Refresh token: httpOnly cookie set by the server for `x-client: web` — never visible to JS.
 * - Refreshes are single-flight; a reused refresh token would sign the user out everywhere.
 */

const TIMEOUT_MS = 15_000;
const REFRESH_MARGIN_MS = 60_000;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

type Session = { accessToken: string; accessTokenExpiresIn: number };

let accessToken: string | null = null;
let expiresAt = 0;
let refreshing: Promise<boolean> | null = null;
let onSignedOut: () => void = () => {};

export function setSignedOutHandler(fn: () => void) {
  onSignedOut = fn;
}

export function acceptSession(s: Session) {
  accessToken = s.accessToken;
  expiresAt = Date.now() + s.accessTokenExpiresIn * 1000;
}

export function dropSession() {
  accessToken = null;
  expiresAt = 0;
}

async function send<T>(method: string, path: string, body?: unknown, token?: string | null): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        'x-client': 'web',
        accept: 'application/json',
        ...(body !== undefined && !(body instanceof FormData) ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
      signal: controller.signal,
    });
  } catch {
    throw new ApiError(0, 'NETWORK', "Can't reach Spendly. Check your connection.");
  } finally {
    clearTimeout(timer);
  }
  const json = await res.json().catch(() => null);
  if (res.ok && json?.success) return json.data as T;
  const err = json?.error;
  throw new ApiError(res.status, err?.code ?? 'HTTP_ERROR', err?.message ?? `Request failed (${res.status})`, err?.details);
}

/** Uses the refresh cookie. Resolves false (signed out) instead of throwing on 401. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      acceptSession(await send<Session>('POST', '/auth/refresh'));
      return true;
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 400)) {
        dropSession();
        return false;
      }
      throw e;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function token(): Promise<string> {
  if (accessToken && expiresAt - Date.now() > REFRESH_MARGIN_MS) return accessToken;
  if (!(await refreshSession())) {
    onSignedOut();
    throw new ApiError(401, 'UNAUTHORIZED', 'Please sign in again');
  }
  return accessToken!;
}

export function publicApi<T>(method: string, path: string, body?: unknown) {
  return send<T>(method, path, body);
}

export async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  try {
    return await send<T>(method, path, body, await token());
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 401) throw e;
    if (!(await refreshSession())) {
      onSignedOut();
      throw e;
    }
    return send<T>(method, path, body, accessToken);
  }
}

/** Authenticated binary download (receipt images) → object URL. */
export async function fetchBlobUrl(path: string): Promise<string> {
  const res = await fetch(`/api/v1${path}`, {
    credentials: 'same-origin',
    headers: { 'x-client': 'web', authorization: `Bearer ${await token()}` },
  });
  if (!res.ok) throw new ApiError(res.status, 'HTTP_ERROR', "Couldn't load the receipt");
  return URL.createObjectURL(await res.blob());
}
